import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useI18n } from 'vue-i18n'
import { toLocaleFormat } from '@/helpers/date-helpers'
import {
  ALL_SIGNATURE_PLAN,
  ALL_SIGNATURE_STATUS,
  SUBSCRIPTION_NAME,
  SOUSCRIPTION_AND_PLAN_LABELS_GTM
} from '@/constants/subscriptions'
import { useRouter, useRoute } from 'vue-router'
import { IconDanger, IconSuccess, IconInfo } from '@accor/icons'
import { useAllPlusSubscription } from './allPlusSubscription'
import { type BadgeVariation } from '@accor/ads-components'

export const useSubscriptions = () => {
  const { t } = useI18n()
  const userStore = useUserStore()
  const router = useRouter()
  const route = useRoute()
  const { activeAllSignatureSubscription, productTier, allSignaturePlanCode } =
    storeToRefs(userStore)

  const { allPlusSubscriptionCardPropsInHub } = useAllPlusSubscription()

  const AllSignatureLabelByPlanCode = {
    [ALL_SIGNATURE_PLAN.SIGPL0]: t('subscription.allSignature.hub.essential'),
    [ALL_SIGNATURE_PLAN.SIGPL1]: t('subscription.allSignature.hub.discover'),
    [ALL_SIGNATURE_PLAN.SIGPL2]: t('subscription.allSignature.hub.explorer'),
    [ALL_SIGNATURE_PLAN.SIGPL3]: t('subscription.allSignature.hub.absolute')
  }

  const getUrl = (name: SUBSCRIPTION_NAME) =>
    router.resolve({
      name: 'subscription-card-details',
      params: {
        cardName: name
      }
    }).href

  const getAllSignatureActionBadge = (status: ALL_SIGNATURE_STATUS) => {
    const badgeByStatus = {
      [ALL_SIGNATURE_STATUS.ACTIVE]: {
        variation: 'success' as BadgeVariation,
        text: t('common.tag.active'),
        prependIcon: IconSuccess
      },
      [ALL_SIGNATURE_STATUS.PAST_DUE]: {
        variation: 'warning' as BadgeVariation,
        text: t('subscription.hub.allSign.tag.pastDue'),
        prependIcon: IconDanger
      },
      [ALL_SIGNATURE_STATUS.PENDING_CANCELLATION]: {
        variation: 'danger' as BadgeVariation,
        text: t('subscription.hub.tag.cancelled'),
        prependIcon: IconDanger
      }
    }
    return badgeByStatus[status]
  }

  const allSignatureSubscriptionDetails = computed(() => {
    return {
      title:
        AllSignatureLabelByPlanCode[
          activeAllSignatureSubscription.value
            ?.plan_code as keyof typeof AllSignatureLabelByPlanCode
        ],
      endValidityDate: toLocaleFormat(activeAllSignatureSubscription.value?.current_period_ends)
    }
  })

  const allSignatureCardPropsInHub = computed(() => {
    if (activeAllSignatureSubscription.value) {
      return {
        ...allSignatureSubscriptionDetails.value,
        image: getAllSignatureImage.value,
        badge: getAllSignatureActionBadge(activeAllSignatureSubscription.value.status),
        url: getUrl(SUBSCRIPTION_NAME.ALL_SIGNATURE),
        type: SUBSCRIPTION_NAME.ALL_SIGNATURE,
        plan: activeAllSignatureSubscription.value?.plan_code
      }
    }
    return undefined
  })

  const getAllSignatureDescriptionBadge = (status: ALL_SIGNATURE_STATUS) => {
    const endDate = toLocaleFormat(activeAllSignatureSubscription.value?.current_period_ends)
    const badgeByStatus = {
      [ALL_SIGNATURE_STATUS.ACTIVE]: {
        prependIcon: IconInfo,
        variation: 'loyalty' as BadgeVariation,
        text: t('validity.date', { date: endDate })
      },
      [ALL_SIGNATURE_STATUS.PAST_DUE]: {
        prependIcon: IconDanger,
        variation: 'warning' as BadgeVariation,
        text: t('subscription.hub.allSign.tag.pastDue')
      },
      [ALL_SIGNATURE_STATUS.PENDING_CANCELLATION]: {
        prependIcon: IconDanger,
        variation: 'danger' as BadgeVariation,
        text: t('subscription.allSignature.cancelledBadge', { periodEnds: endDate })
      }
    }
    return badgeByStatus[status]
  }

  const allSignatureSubscriptionCardPropsInCardBlock = computed(() => {
    if (activeAllSignatureSubscription.value) {
      return {
        ...allSignatureSubscriptionDetails.value,
        descriptionBadge: getAllSignatureDescriptionBadge(
          activeAllSignatureSubscription.value.status
        )
      }
    }
    return undefined
  })

  const allSigImage = import.meta.glob('../assets/images/all-signature/*.png', {
    eager: true,
    import: 'default'
  }) as Record<string, string>

  const getAllSignatureImage = computed(() => {
    return productTier.value && activeAllSignatureSubscription.value?.plan_code
      ? allSigImage[
          `../assets/images/all-signature/${productTier.value}_${activeAllSignatureSubscription.value?.plan_code}.png`
        ]
      : ''
  })

  const AllPlusAndAllSignatureSubscriptionCards = computed(() => [
    ...allPlusSubscriptionCardPropsInHub.value,
    ...(allSignatureCardPropsInHub.value ? [allSignatureCardPropsInHub.value] : [])
  ])

  const getPlanFullName = (
    cardName: ALL_SIGNATURE_PLAN | SUBSCRIPTION_NAME,
    plan: ALL_SIGNATURE_PLAN | SUBSCRIPTION_NAME = cardName
  ) =>
    SOUSCRIPTION_AND_PLAN_LABELS_GTM[
      cardName === SUBSCRIPTION_NAME.ALL_SIGNATURE ? plan : cardName
    ] || ''

  const subscriptionCardName = computed(() => (route.params.cardName as SUBSCRIPTION_NAME) || '')

  const planLabel = computed(() =>
    getPlanFullName(subscriptionCardName.value, allSignaturePlanCode.value)
  )

  return {
    AllPlusAndAllSignatureSubscriptionCards,
    allSignatureSubscriptionCardPropsInCardBlock,
    getAllSignatureImage,
    getPlanFullName,
    planLabel
  }
}
